<template>
  <div class="instruction-panel-entry" style="height: 100%">
    <h2 class="h2">{{ $t("instruction-panel.contacts.title") }}</h2>

    <ContactModale
      :open="openContactModale"
      total="1"
      :projet-id="project.projetId"
      :destinataires="contacts"
      @close-modale="openContactModale = false"
    />

    <b-button
      v-if="!isObservateurNational"
      class="mt-5"
      type="is-info"
      @click="openContactModale = true"
    >
      {{ $t("instruction-panel.contacts.contact") }}
    </b-button>

    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="contacts"
      :class="{ filtered: activeFilters.length }"
      class="mt-5 contact-table"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="(column, i) in columns"
        :key="i"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'prenom-nom-profil'">
            <div class="columns">
              <div class="column pb-0">
                {{ props.row["prenom"] + " " + props.row["nom"] }}
              </div>
            </div>
            <div class="columns">
              <div class="column pt-0">{{ $t(props.row["profil"]) }}</div>
            </div>
          </div>
          <div v-if="column.field === 'coordonnees'">
            <div class="columns">
              <div class="column pb-0" style="word-break: break-all">
                {{ props.row["courriel"] }}
              </div>
            </div>
            <div class="columns">
              <div class="column pt-0">{{ props.row["telephone"] }}</div>
            </div>
          </div>
        </template>
      </b-table-column>

      <template #empty>
        <div class="empty-table">
          {{ $t("instruction-panel.contacts.aucun-contact") }}
        </div>
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">
import { FilterDTO, ProjetAnnuelDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { defineComponent, PropType } from "vue"
import { useTableView } from "@/hooks/useTableView"
import Column from "@/views/tables/Column"
import ContactModale from "@/views/commons/ContactModale.vue"
import { UsersService } from "@/services/rest/users/UsersService"
import { LoginService } from "@/services/rest/login/LoginService"
import { BButton, BTable, BTableColumn } from "buefy"

export default defineComponent({
  name: "InstructionPanelContacts",
  components: {
    ContactModale,
    BButton,
    BTable,
    BTableColumn,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
  },
  data() {
    return {
      openContactModale: false,
      isObservateurNational: false,
      contacts: [] as UtilisateurDTO[],
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      tableView: null as any,
    }
  },

  computed: {
    // Hook state as computed properties
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  watch: {
    project(): void {
      this.concatContacts()
    },
  },
  created(): void {
    // Initialize hook with proper binding
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "instruction-panel-contacts-table",
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: () => [],
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    const loggedUser = this.loginService.getLoggedUser()
    if (loggedUser) {
      this.isObservateurNational = this.usersService.isObservateurNational(loggedUser)
    }
  },
  mounted(): void {
    this.tableView?.initMounted()
  },
  methods: {
    // Hook method delegation
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage, 10) : newPage
      this.tableView?.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: "asc" | "desc"): void {
      if (field) {
        this.tableView?.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView?.sortingPriorityRemoved(field)
      }
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    async reloadData(): Promise<void> {
      this.concatContacts()
      this.updateTotal(this.contacts.length)
    },

    concatContacts(): void {
      const enseignants = this.project.enseignants
        ? this.project.enseignants.filter((e) => !this.invited(e) && e.statutActivation === "ACTIF")
        : []
      const referents = this.project.referents
        ? this.project.referents.filter((r) => !this.invited(r) && r.statutActivation === "ACTIF")
        : []
      this.contacts = enseignants.concat(referents)
    },

    getInitialColumns(): Array<Column> {
      return [
        {
          field: "prenom-nom-profil",
          label: this.$t("instruction-panel.contacts.prenom-nom-profil").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: false,
        },
        {
          field: "coordonnees",
          label: this.$t("instruction-panel.contacts.coordonnees").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: false,
        },
      ]
    },

    invited(user: UtilisateurDTO): boolean {
      if (user.id) {
        return this.project.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },
  },
})
</script>

<style lang="scss">
.contact-table {
  height: 75%;

  div.table-wrapper:nth-child(1) {
    height: 100% !important;
    max-height: 100% !important;
  }
}
</style>
